import React from "react";

import { Box, Button, CircularProgress } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

import { CustomVideoModalStep } from "../../../types";

interface RenderActionsProps {
  currentStep: CustomVideoModalStep;
  prompt: string;
  handleClose: () => void;
  handleGenerateOptions: () => void;
  handleBackToInput: () => void;
  handleBackToOptions: () => void;
  handleFinalSubmit: () => void;
  inProgress: boolean;
  trimmedEnd: number;
  trimmedStart: number;
  hasCredits: boolean;
}

export const renderActions = (renderActionsProps: RenderActionsProps) => {
  const {
    currentStep,
    prompt,
    handleClose,
    handleGenerateOptions,
    handleBackToInput,
    inProgress,
    trimmedEnd,
    trimmedStart,
    handleBackToOptions,
    handleFinalSubmit,
    hasCredits,
  } = renderActionsProps;

  switch (currentStep) {
    case "input":
      return (
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={inProgress}
            sx={{
              "&:disabled": {
                background: "#e2e8f0 !important",
                color: "#64748b !important",
                boxShadow: "none !important",
                transform: "none",
                border: "1px solid #cbd5e1 !important",
                "&.MuiButton-contained": {
                  background: "#e2e8f0 !important",
                  color: "#64748b !important",
                },
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateOptions}
            disabled={!prompt.trim() || inProgress || !hasCredits}
            startIcon={inProgress ? <CircularProgress size={20} /> : null}
            sx={{
              background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
              boxShadow: "0 4px 12px rgba(139, 92, 246, 0.3)",
              fontWeight: 600,
              "&:hover": {
                background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                boxShadow: "0 6px 20px rgba(139, 92, 246, 0.4)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                background: "#e2e8f0 !important",
                color: "#64748b !important",
                boxShadow: "none !important",
                transform: "none",
                border: "1px solid #cbd5e1 !important",
                "&.MuiButton-contained": {
                  background: "#e2e8f0 !important",
                  color: "#64748b !important",
                },
              },
            }}
          >
            {inProgress ? "Generating..." : "Generate Short Video Ideas"}
          </Button>
        </Box>
      );
    case "suggested-videos":
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <Button variant="contained" onClick={handleBackToInput}>
            Back to Edit Prompt
          </Button>
        </Box>
      );
    case "preview":
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Button variant="outlined" onClick={handleBackToOptions}>
            Back to Options
          </Button>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleFinalSubmit}
              disabled={inProgress || trimmedEnd - trimmedStart > 120}
              startIcon={
                inProgress ? (
                  <CircularProgress
                    size={20}
                    sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                  />
                ) : (
                  <CheckIcon />
                )
              }
              sx={{
                background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                fontWeight: 600,
                "&:hover": {
                  background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                  boxShadow: "0 6px 20px rgba(59, 130, 246, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:disabled": {
                  background: "#e2e8f0 !important",
                  color: "#64748b !important",
                  boxShadow: "none !important",
                  transform: "none",
                  border: "1px solid #cbd5e1 !important",
                  "&.MuiButton-contained": {
                    background: "#e2e8f0 !important",
                    color: "#64748b !important",
                  },
                },
              }}
            >
              {inProgress ? "Generating..." : "Save Video"}
            </Button>
          </Box>
        </Box>
      );
    default:
      return null;
  }
};
